/**
 * MESF Theme - Centralized AG Grid Styling System
 *
 * This module centralizes ALL AG Grid styling to ensure consistency
 * across the application. Import `mesfTheme` for grid theme and
 * `globalDefaultColDef` for default column definitions.
 *
 * Usage:
 *   import { mesfTheme, globalDefaultColDef, mesfColors } from "@setup/ag-grid/mesf-theme";
 *
 *   <AgGridReact
 *     theme={mesfTheme}
 *     defaultColDef={globalDefaultColDef}
 *     ...
 *   />
 */

import { createPart, themeMaterial } from "ag-grid-enterprise";

/**
 * Custom CSS styles for AG Grid
 * Includes header styling, cell alignment, checkbox styling, and context menu
 */
const mesfCustomStyles = createPart({
	css: `
        /* The cell focus ring is never wanted, on any grid in this application.
           These are reports: clicking a cell means nothing, and the outline reads
           as an editable field. Killed here rather than with suppressCellFocus on
           each grid so it cannot come back on the next page somebody adds.
           border-color rather than border, so nothing shifts by a pixel. */
        .ag-cell-focus,
        .ag-cell-focus:focus,
        .ag-cell-focus:focus-within,
        .ag-cell-range-single-cell,
        .ag-has-focus .ag-cell-focus,
        .ag-full-width-row.ag-row-focus .ag-cell-wrapper.ag-row-group {
            border-color: transparent !important;
            outline: none !important;
            box-shadow: none !important;
        }

        /* Header cell styling */
        .ag-header-cell-label {
            justify-content: center !important;
            display: flex !important;
            align-items: center !important;
            text-align: center !important;
            padding: 0px !important;
            font-size: 12px;
        }

        .ag-header-cell {
            white-space: normal !important;
            word-wrap: break-word !important;
            padding: 0px !important;
            text-align: center !important;
            font-size: 0.625rem;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .ag-header-group-cell-label {
            font-size: 0.75rem;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
        }

        /* Remove horizontal border between group header row and child header row */
        .ag-header-row {
            border-top: none !important;
            border-bottom: none !important;
        }
        .ag-header-cell {
            border-top: none !important;
            border-bottom: none !important;
        }
        .ag-header-group-cell {
            border-top: none !important;
            border-bottom: none !important;
        }

        /* Group separator - subtle vertical border between header groups */
        .ag-header-cell.ag-column-group-separator {
            border-right: 1px solid #e0e0e0 !important;
        }
        .ag-header-group-cell.ag-column-group-separator {
            border-right: 1px solid #e0e0e0 !important;
        }

        /* Hide header menu icon */
        .ag-header-icon {
            display: none !important;
        }

        /* A column whose filter is meant to be reachable opts back in. The rule
           above hides the filter funnel along with the menu, which leaves a
           filterable column with no way for anyone to open its filter. */
        .ag-header-cell.ag-header-with-menu .ag-header-icon {
            display: flex !important;
        }

        /* Checkbox styling */
        .ag-checkbox-input-wrapper {
            margin-left: 9px !important;
        }

        .ag-checkbox-input-wrapper.ag-checked {
            background-color: #00008B !important;
            border-color: #00008B !important;
        }

        .ag-cell .ag-checkbox-input-wrapper {
            margin-left: 12px !important;
        }

        /* Cell styling */
        .ag-cell {
            white-space: normal !important;
            word-wrap: break-word !important;
            line-height: 1.4 !important;
            text-align: center !important;
            border: none;
            padding: 0px !important;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .ag-row {
            border-bottom: 1px solid #e0e0e0;
        }

        /* Inactive row styling (Active = false) */
        .ag-row-inactive {
            background-color: #f5f5f5 !important;
            color: #9e9e9e !important;
        }
        .ag-row-inactive:hover {
            background-color: #eeeeee !important;
        }

        .ag-cell-wrapper {
            height: auto !important;
            min-height: 32px !important;
        }

        /* Row hover effects for action buttons */
        .ag-row-hover .buttons {
            opacity: 1;
        }
        .ag-row-focus .buttons {
            opacity: 1;
        }
        .ag-row-selected .buttons {
            opacity: 1;
        }

        /* Stand grid: disable row hover, only editable cells get hover */
        .ag-row-hover .stand-cell,
        .ag-row.ag-row-hover .stand-cell {
            background-color: inherit !important;
        }
        .stand-cell-editable:hover {
            background-color: rgba(0, 0, 0, 0.04) !important;
            cursor: pointer;
        }

        /* Context menu styling */
        .ag-menu {
            padding: 4px !important;
            background: #ffffff !important;
            border: 1px solid #e0e0e0 !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
            min-width: 180px !important;
        }

        .ag-menu-list {
            padding: 4px !important;
        }

        .ag-menu-option {
            padding: 6px 10px !important;
            border-radius: 2px !important;
            margin: 2px 0 !important;
            font-size: 13px !important;
            color: #333333 !important;
            background: transparent !important;
            transition: all 0.2s ease !important;
        }

        .ag-menu-option:hover {
            background: #f5f5f5 !important;
            color: #00008B !important;
        }

        .ag-menu-option-disabled {
            color: #999999 !important;
            opacity: 0.6 !important;
        }

        .ag-menu-separator {
            border-top: 1px solid #e0e0e0 !important;
            margin: 4px 0 !important;
        }

        .ag-menu .ag-icon {
            color: #666666 !important;
            fill: #666666 !important;
        }
    `,
});

/**
 * MESF Theme for AG Grid
 * Based on Material theme with custom styling
 */
export const mesfTheme = themeMaterial
	.withParams({
		spacing: 1,
		headerFontSize: 10,
		rowHeight: 35,
		headerHeight: 32,
	})
	.withPart(mesfCustomStyles);

/**
 * Global default column definitions for AG Grid
 * Use this as defaultColDef prop in AgGridReact
 */
export const globalDefaultColDef = {
	wrapText: true,
	autoHeight: true,
	wrapHeaderText: true,
	resizable: true,
	cellStyle: {
		textAlign: "center",
		fontSize: "12px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		border: "none",
	},
};

/**
 * Global default grid options for AG Grid
 * Use spread operator to apply these to AgGridReact
 *
 * Usage:
 *   <AgGridReact
 *     {...globalGridOptions}
 *     ...
 *   />
 */
export const globalGridOptions = {
	suppressContextMenu: true,
};

/**
 * Centralized color palette for the application
 * Use these colors for consistency across components
 */
export const mesfColors = {
	primary: {
		main: "#3f51b5", // Indigo - primary brand color
		dark: "#303f9f",
		light: "#7986cb",
	},
	secondary: {
		main: "#00008B", // Dark blue - accent color
		dark: "#00007B",
	},
	info: {
		main: "#3b82f6",
		dark: "#2563eb",
	},
	success: {
		main: "#10b981",
		dark: "#059669",
	},
	warning: {
		main: "#f59e0b",
		dark: "#d97706",
	},
	error: {
		main: "#ef4444",
		dark: "#dc2626",
	},
	background: {
		paper: "#ffffff",
		default: "#f8fafc",
		highlight: "#C8DEF7", // Row hover/selection background
	},
	text: {
		primary: "#1e293b",
		secondary: "#64748b",
	},
	grey: {
		50: "#f8fafc",
		100: "#f1f5f9",
		200: "#e2e8f0",
		300: "#cbd5e1",
		400: "#94a3b8",
		500: "#64748b",
		divider: "#e2e8f0",
	},
	common: {
		white: "#ffffff",
		black: "#000000",
	},
	action: {
		hover: "#f1f5f9",
		selected: "#C8DEF7",
	},
	// Category colors for delays/stops
	category: {
		production: "#4caf50", // Green - P
		maintenance: "#f44336", // Red - M
		electrical: "#2196f3", // Blue - E
		changeover: "#ff9800", // Orange - C
	},
	shadows: {
		2: "0 2px 8px rgba(0, 0, 0, 0.08)",
		4: "0 4px 16px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
		8: "0 6px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.08)",
	},
} as const;

/**
 * Utility function to convert hex color to rgba
 * @param hex - Hex color code (e.g., "#ffffff")
 * @param opacity - Opacity value between 0 and 1
 * @returns rgba string
 */
export const hexToRgba = (hex: string, opacity: number): string => {
	const r = Number.parseInt(hex.slice(1, 3), 16);
	const g = Number.parseInt(hex.slice(3, 5), 16);
	const b = Number.parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};
